import { Controller } from "@hotwired/stimulus"

const REFRESH_INTERVAL = 10000;
const ARTWORK_DOWNLOAD_RETRY_INTERVAL = 10000;
const ARTWORK_DOWNLOAD_MAX_ATTEMPTS = 60;

export default class extends Controller {
  static targets = ["downloadStatus", "pageStatus"];

  static values = {
    indexLastCheckbox: Number,
  };

  connect() {
    document.body.addEventListener('click', (e) => {
      if (!e.target.closest('.no-close-dropdowns')) this.closeAllDropdowns();
    });

    this.reloadInterval = setInterval(this.reloadOrders, REFRESH_INTERVAL);
  }

  disconnect() {
    if (this.reloadInterval) clearInterval(this.reloadInterval);
    if (this.downloadResetTimeout) clearTimeout(this.downloadResetTimeout);
  }

  closeAllDropdowns() {
    document.querySelectorAll('.orders .dropdown[open]').forEach(dropdown => dropdown.removeAttribute('open'));
  }

  checkbox(event) {
    const checkedOrder = event.target.closest('.order');

    if (event.shiftKey && event.target.checked) {
      const currentIndex = [...checkedOrder.parentElement.children].indexOf(checkedOrder);
      const orders = event.target.closest('.orders').children;

      const min = Math.min(currentIndex, this.indexLastCheckboxValue);
      const max = Math.max(currentIndex, this.indexLastCheckboxValue);
      let i = 0;

      for (const order of orders) {
        if (i >= min && i < max)
          order.querySelector('input').checked = true;

        i++;
      }

    } else if (event.target.checked) {
      this.indexLastCheckboxValue = [...checkedOrder.parentElement.children].indexOf(checkedOrder);
    }

    const checkedCheckboxes = document.querySelectorAll('.orders input[name="_checkboxes[]"]:checked');
    const checkedOrderIds = Array.from(checkedCheckboxes).map(checkbox => {
      return checkbox.closest('.order[data-shopify-order-id]').dataset.shopifyOrderId;
    }).filter(e => e);

    document.getElementById('shopify_order_ids').value = checkedOrderIds.join(',');
  }

  async reUpload(event) {
    event.preventDefault();
    event.target.closest('.dropdown[open]').removeAttribute('open');

    const order = event.target.closest('.order');

    this.setUploadState(order, 'uploading');

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    const response = await fetch('/orders/reupload_files', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        authenticity_token: csrfToken,
        shopify_order_id: order.dataset.shopifyOrderId
      }),
    });

    if (!response.ok) this.setUploadState(order, 'failed');
  }

  async regenerateArtwork(event) {
    event.preventDefault();
    event.target.closest('.dropdown[open]')?.removeAttribute('open');

    const order = event.target.closest('.order');

    try {
      await this.regenerateArtworkForOrder(order);
      this.showDownloadStatus('Regenerating V1 labels, V2 labels, and V2 NEST artwork on the server.');
      setTimeout(() => this.resetDownloadState(), 5000);
    } catch (_error) {
      this.setUploadState(order, 'failed');
      this.showDownloadStatus('File regeneration could not be queued.');
      setTimeout(() => this.resetDownloadState(), 6000);
    }
  }

  async downloadArtwork(event) {
    event.preventDefault();
    const link = event.currentTarget;
    const order = link.closest('.order');

    this.closeAllDropdowns();
    this.showDownloadStatus('Preparing artwork download. If needed, V2 artwork will regenerate first.');
    this.setDownloadControlLoading(link);

    try {
      let response = await this.fetchArtworkDownload(link.href);

      if (response.status === 404) {
        await this.regenerateArtworkForOrder(order);
        this.showDownloadStatus('V2 artwork is regenerating. The download will start when it is ready.');
        response = await this.waitForArtworkDownload(link.href);
      }

      if (!response.ok) throw new Error('Artwork download failed');

      await this.saveDownloadResponse(response, 'MIW-Artwork.zip');
      this.setUploadState(order, 'succeeded');
      this.showDownloadStatus('Artwork download started.');
      setTimeout(() => this.resetDownloadState(), 2500);
    } catch (_error) {
      this.setUploadState(order, 'failed');
      this.showDownloadStatus('Artwork download could not be prepared. Try Regenerate all files.');
      setTimeout(() => this.resetDownloadState(), 6000);
    }
  }

  async regenerateArtworkForOrder(order) {
    this.setUploadState(order, 'uploading');

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    const response = await fetch('/orders/v2/regenerate_artwork', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        authenticity_token: csrfToken,
        shopify_order_id: order.dataset.shopifyOrderId
      }),
    });

    if (!response.ok) throw new Error('File regeneration failed');

    return response;
  }

  fetchArtworkDownload(url) {
    return fetch(url, {
      headers: {
        Accept: 'application/zip',
      },
    });
  }

  async waitForArtworkDownload(url) {
    for (let attempt = 0; attempt < ARTWORK_DOWNLOAD_MAX_ATTEMPTS; attempt++) {
      await this.sleep(ARTWORK_DOWNLOAD_RETRY_INTERVAL);
      const response = await this.fetchArtworkDownload(url);
      if (response.status !== 404) return response;
    }

    throw new Error('Timed out waiting for V2 artwork');
  }

  async saveDownloadResponse(response, fallbackFilename) {
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = this.downloadFilename(response, fallbackFilename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }

  downloadFilename(response, fallbackFilename) {
    const disposition = response.headers.get('Content-Disposition') || '';
    const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encodedFilename) return decodeURIComponent(encodedFilename);

    return disposition.match(/filename="?([^";]+)"?/i)?.[1] || fallbackFilename;
  }

  sleep(milliseconds) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  async batchRegenerate(event) {
    event.preventDefault();

    const checkedCheckboxes = document.querySelectorAll('.orders input[name="_checkboxes[]"]:checked');
    const checkedOrderIds = Array.from(checkedCheckboxes).map(checkbox => {
      return checkbox.closest('.order[data-shopify-order-id]').dataset.shopifyOrderId;
    }).filter(e => e);

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    const response = await fetch('/orders/v2/batch_regenerate_artwork', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        authenticity_token: csrfToken,
        shopify_order_ids: checkedOrderIds,
      }),
    });

    Array.from(checkedCheckboxes).forEach(checkbox => {
      const order = checkbox.closest('.order');

      if (response.ok) this.setUploadState(order, 'uploading');
      else this.setUploadState(order, 'failed');
    });

    if (response.ok) {
      this.showDownloadStatus('Regenerating selected orders on the server.');
      setTimeout(() => this.resetDownloadState(), 5000);
    } else {
      this.showDownloadStatus('Selected orders could not be queued for regeneration.');
      setTimeout(() => this.resetDownloadState(), 6000);
    }
  }

  startDownload(event) {
    const control = event.submitter || event.currentTarget;

    this.closeAllDropdowns();
    this.showDownloadStatus();
    this.setDownloadControlLoading(control);

    if (this.downloadResetTimeout) clearTimeout(this.downloadResetTimeout);
    this.downloadResetTimeout = setTimeout(() => this.resetDownloadState(), 30000);
  }

  submitLocationFilter(event) {
    event.preventDefault();

    const select = event.currentTarget;
    const form = select.form;
    const url = new URL(form.action || window.location.pathname, window.location.origin);
    const params = new URLSearchParams(new FormData(form));

    params.delete('after');
    params.delete('before');
    params.delete('location');

    if (select.value) params.set('location', select.value);

    const queryString = params.toString();
    this.showPageStatus('Loading orders for selected location...');
    Turbo.visit(`${url.pathname}${queryString ? `?${queryString}` : ''}`);
  }

  startPageRefresh(event) {
    const form = event.currentTarget;
    const select = form.querySelector('select');

    if (select) select.setAttribute('aria-disabled', 'true');
    this.showPageStatus('Loading orders for selected location...');
  }

  async loadFileSummary(event) {
    const details = event.currentTarget;

    if (!details.open || details.dataset.loaded === "true" || details.dataset.loading === "true") return;

    const content = details.querySelector("[data-file-summary-content]");
    const total = details.querySelector("[data-file-summary-total]");
    details.dataset.loading = "true";
    if (content) content.textContent = "Loading file sizes...";

    try {
      const response = await fetch(details.dataset.url);
      if (!response.ok) throw new Error("File summary request failed");

      const data = await response.json();
      details.dataset.loaded = "true";

      if (total && data.total_display_size) total.textContent = data.total_byte_size ? data.total_display_size : "Unknown total size";
      this.renderFileSummary(content, data.files || []);
    } catch (_error) {
      if (content) content.textContent = "Could not load file sizes.";
    } finally {
      delete details.dataset.loading;
    }
  }

  renderFileSummary(content, files) {
    if (!content) return;

    content.replaceChildren();

    if (files.length === 0) {
      content.textContent = "No print files found.";
      return;
    }

    const list = document.createElement("ul");
    list.className = "miw-polaris-file-list";

    files.forEach((file) => {
      const item = document.createElement("li");
      const details = document.createElement("span");
      const filename = document.createElement("span");
      const size = document.createElement("span");

      details.className = "miw-polaris-file-details";
      filename.className = "miw-polaris-file-name";
      filename.textContent = file.filename || file.original_filename || "Unnamed file";
      size.className = "miw-polaris-file-size";
      size.textContent = file.display_size || "Unknown";

      if (file.preview_image_url) {
        const previewLink = document.createElement("a");
        const previewImage = document.createElement("img");

        item.classList.add("has-preview");
        previewLink.className = "miw-polaris-file-preview-link";
        previewLink.href = file.preview_image_url;
        previewLink.target = "_blank";
        previewLink.rel = "noopener";
        previewLink.setAttribute("aria-label", `Open preview for ${file.filename || file.original_filename || "print file"}`);

        previewImage.className = "miw-polaris-file-preview-image";
        previewImage.src = file.preview_image_url;
        previewImage.alt = "";
        previewImage.loading = "lazy";

        previewLink.appendChild(previewImage);
        item.appendChild(previewLink);
      }

      details.appendChild(filename);

      if (file.warning_message) {
        const warning = document.createElement("span");
        warning.className = "miw-polaris-file-warning";
        warning.textContent = file.warning_message;
        details.appendChild(warning);
      }

      item.append(details, size);
      list.appendChild(item);
    });

    content.appendChild(list);
  }

  showDownloadStatus(message = 'Preparing label download. The file will start shortly.') {
    if (!this.hasDownloadStatusTarget) return;

    this.downloadStatusTarget.hidden = false;
    this.downloadStatusTarget.querySelector('span').textContent = message;
  }

  showPageStatus(message) {
    if (!this.hasPageStatusTarget) return;

    this.pageStatusTarget.hidden = false;
    this.pageStatusTarget.querySelector('span').textContent = message;
  }

  setDownloadControlLoading(control) {
    if (!control) return;

    this.downloadControl = control;
    control.classList.add('miw-polaris-button-loading');

    if (control.tagName === 'INPUT') {
      control.dataset.originalValue = control.value;
      control.value = 'Preparing...';
      control.disabled = true;
    } else {
      control.dataset.originalText = control.textContent;
      control.textContent = 'Preparing...';
      control.setAttribute('aria-disabled', 'true');
    }
  }

  resetDownloadState() {
    if (this.hasDownloadStatusTarget) this.downloadStatusTarget.hidden = true;

    if (!this.downloadControl) return;

    const control = this.downloadControl;
    control.classList.remove('miw-polaris-button-loading');

    if (control.tagName === 'INPUT') {
      control.value = control.dataset.originalValue || 'Download selected';
      control.disabled = false;
    } else {
      control.textContent = control.dataset.originalText || 'Download';
      control.removeAttribute('aria-disabled');
    }

    this.downloadControl = null;
  }

  async reloadOrders() {
    const ordersList = document.getElementById('orders-list');
    const orderIds = Array.from(ordersList.getElementsByClassName('order')).map(order => order.dataset.orderId).filter(e => e);

    if (orderIds.length === 0) return;

    const response = await fetch('/orders/view_data?order_ids=' + orderIds.join(','));

    response.json().then((orders) => {
      for (const order of orders) {
        const orderElement = ordersList.querySelector(`.order[data-order-id='${order.id}']`);
        if (!orderElement) continue;

        if (order.upload_state === 'none' || !order.num_print_files) this.setUploadState(orderElement, 'none');
        else this.setUploadState(orderElement, order.upload_state);

        const warningIcon = orderElement.querySelector('.v2-warning-state');
        warningIcon?.classList.toggle('is-hidden', !order.v2_warning);
      }
    });
  }

  setUploadState(order, state) {
    const badge = order?.querySelector('.upload-state');
    if (!badge) return;

    badge.dataset.uploadState = state || 'none';
    badge.innerHTML = this.uploadStateIconSvg(state || 'none');
  }

  uploadStateIconSvg(state) {
    const icon = {
      pending: '<circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 3"></path>',
      uploading: '<path d="M12 16V4"></path><path d="M7 9l5-5 5 5"></path><path d="M5 20h14"></path>',
      succeeded: '<path d="M20 6L9 17l-5-5"></path>',
      failed: '<path d="M12 4l9 16H3L12 4z"></path><path d="M12 9v5"></path><path d="M12 17h.01"></path>',
      none: '<circle cx="12" cy="12" r="8"></circle><path d="M7.8 7.8l8.4 8.4"></path>',
    }[state] || '<circle cx="12" cy="12" r="8"></circle><path d="M7.8 7.8l8.4 8.4"></path>';

    return `<svg class="miw-polaris-icon" fill="none" aria-hidden="true" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24">${icon}</svg>`;
  }

  showPage(event) {
    event.target.disabled = true;
    Turbo.visit(event.target.dataset.href);
  }
}
